import { useCallback, useEffect } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import LibraryModal from "../track/LibraryModal.js";
import NotFoundPage from "../../routes/NotFoundPage.js";
import { useDetailNavigation } from "../../hooks/useDetailNavigation.js";
import { useDocumentMeta } from "../../hooks/useDocumentMeta.js";
import { useContentMetadata } from "../../hooks/useCustomQuery.js";
import { buildContentPath, slugify } from "../../utils/detailPaths.js";
import { getYear } from "../../utils/utils.js";

// Movie/book metadata carries its own title; a TV content id may be the series
// or one of its seasons, each with a different title, so it's looked up by id
// among the seasons useContentMetadata already fetched.
function getDisplayTitle(contentType, contentId, metadata) {
  if (!metadata) return null;
  if (contentType === "tvshow") {
    return (
      metadata.seasons?.find((s) => s.content_id === contentId)?.title ?? null
    );
  }
  return metadata.title ?? null;
}

// Director/author/creator — a single "person" credit for the tab title. TV
// credits (creators) live on the series-level "series" object since they
// don't vary per season; movie/book credits are already flat on metadata.
function getDisplayPerson(contentType, metadata) {
  if (!metadata) return null;
  const persons =
    contentType === "tvshow" ? metadata.series?.persons : metadata.persons;
  return persons?.[0] ?? null;
}

// Year lives in a different field per content type. For TV this is already
// season-1-as-series-fallback resolved server-side (see get_tvshow_details in
// flask-server/app/services/tvshow.py), so a season *and* the series entry both
// carry a usable air_date with no extra lookup needed here.
function getDisplayYear(contentType, contentId, metadata) {
  if (!metadata) return null;
  if (contentType === "tvshow") {
    const airDate = metadata.seasons?.find(
      (s) => s.content_id === contentId,
    )?.air_date;
    return getYear(airDate);
  }
  if (contentType === "movie") return getYear(metadata.details?.release_date);
  if (contentType === "book")
    return metadata.details?.first_published_year ?? null;
  return null;
}

/**
 * Single mount point for LibraryModal — the movie/tv/book detail dialog.
 * contentType is fixed per route (see router.js); contentId, slug and entry
 * come from the URL, which is the only place this identity is held.
 */
export default function ContentDetailRoute({ contentType }) {
  const { contentId: contentIdParam, slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { closeDetail } = useDetailNavigation();

  const contentId = Number(contentIdParam);
  const isValidId = Number.isInteger(contentId) && contentId > 0;
  const entryIdParam = searchParams.get("entry");
  const entryId = entryIdParam ? Number(entryIdParam) : null;

  // Hooks must run unconditionally — an invalid id is handled by the return
  // below, not by skipping hooks, since the same route element persists across
  // a param change and can't vary its hook count between renders.
  const { data: metadata } = useContentMetadata(
    contentType,
    isValidId ? contentId : undefined,
  );
  const title = getDisplayTitle(contentType, contentId, metadata);
  const year = getDisplayYear(contentType, contentId, metadata);
  const person = getDisplayPerson(contentType, metadata);
  useDocumentMeta({
    title: title
      ? `${title}${year ? ` (${year})` : ""}${person ? ` by ${person}` : ""} on Curate`
      : undefined,
  });

  useEffect(() => {
    if (!isValidId || !title) return;
    const canonicalSlug = slugify(title);
    if (slug !== canonicalSlug) {
      // Preserve the query string (e.g. ?entry=<id> from a shared link) —
      // this redirect only fixes the slug, it isn't a navigation away from
      // whatever the URL was already pointing at.
      navigate(
        `${buildContentPath({ contentType, contentId, title })}${location.search}`,
        {
          replace: true,
          state: location.state,
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValidId, title, slug, contentId, contentType]);

  // TV season switching. Lives here rather than in LibraryModal because this is
  // where the URL is owned and where metadata (hence each season's title) is
  // already loaded — so a switch lands on the canonical path in one hop instead
  // of arriving slug-less and being corrected by the effect above.
  //
  // The equality guard matters: TVShowCard redirects single-season shows to S1
  // on mount, which for a show already opened at season level asks to navigate
  // to the id we're already on. Without the guard that navigation strips the
  // slug, the effect above puts it back, and the two ping-pong forever — the
  // re-render recreates this callback, which re-fires TVShowCard's effect.
  // `replace` so Back closes the modal rather than walking back through seasons.
  const handleContentChange = useCallback(
    (newContentId) => {
      if (newContentId === contentId) return;
      navigate(
        buildContentPath({
          contentType,
          contentId: newContentId,
          title: getDisplayTitle(contentType, newContentId, metadata),
        }),
        { replace: true, state: location.state },
      );
    },
    [contentId, contentType, metadata, navigate, location.state],
  );

  // Drops ?entry=<id> while staying on the same content — leaving a focused or
  // scoped entry for the normal view. `replace` because it's a change of view
  // rather than a navigation: Back should still close the modal.
  const handleClearEntry = useCallback(() => {
    if (!entryId) return;
    navigate(location.pathname, { replace: true, state: location.state });
  }, [entryId, navigate, location.pathname, location.state]);

  if (!isValidId) return <NotFoundPage />;

  return (
    <LibraryModal
      open
      contentType={contentType}
      contentId={contentId}
      entryId={entryId}
      onClearEntry={handleClearEntry}
      onContentChange={handleContentChange}
      onClose={closeDetail}
    />
  );
}
